import React, { useState, useEffect } from 'react';
import { DieselLayout } from '../../components/diesel/DieselLayout';
import { useDiesel } from '../../context/DieselContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import {
    Users,
    Plus,
    Search,
    Pencil,
    Trash2,
    RefreshCw,
    Phone,
    Mail,
    CreditCard,
    AlertTriangle,
} from 'lucide-react';
import { ImportarExcel } from '../../components/diesel/ImportarExcel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DieselConductoresPage() {
    const { hasPermission } = useDiesel();
    const [conductores, setConductores] = useState([]);
    const [tiposLicencia, setTiposLicencia] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [dialogOpen, setDialogOpen] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);

    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [res, licRes] = await Promise.all([
                fetch(`${API_URL}/api/diesel/conductores`, { headers }),
                fetch(`${API_URL}/api/diesel/tipos-licencia`, { headers }),
            ]);
            if (res.ok) setConductores(await res.json());
            if (licRes.ok) setTiposLicencia(await licRes.json());
        } catch (err) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const filteredConductores = conductores.filter(c =>
        c.nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.rut?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSave = async () => {
        if (!formData.rut || !formData.nombre) {
            toast.error('Complete RUT y nombre');
            return;
        }

        setSaving(true);
        try {
            const method = formData.id ? 'PUT' : 'POST';
            const url = formData.id 
                ? `${API_URL}/api/diesel/conductores/${formData.id}`
                : `${API_URL}/api/diesel/conductores`;

            const res = await fetch(url, { method, headers, body: JSON.stringify(formData) });

            if (res.ok) {
                toast.success(formData.id ? 'Conductor actualizado' : 'Conductor creado');
                setDialogOpen(false);
                setFormData({});
                loadData();
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Error al guardar');
            }
        } catch (err) {
            toast.error('Error al guardar');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (conductor) => {
        if (!confirm(`¿Eliminar conductor ${conductor.nombre}?`)) return;

        try {
            const res = await fetch(`${API_URL}/api/diesel/conductores/${conductor.id}`, {
                method: 'DELETE',
                headers,
            });

            if (res.ok) {
                const data = await res.json();
                toast.success(data.message);
                loadData();
            }
        } catch (err) {
            toast.error('Error al eliminar');
        }
    };

    const isLicenciaVencida = (fecha) => {
        if (!fecha) return false;
        return new Date(fecha) < new Date();
    };

    const isLicenciaPorVencer = (fecha) => {
        if (!fecha) return false;
        const vencimiento = new Date(fecha);
        const hoy = new Date();
        const en30Dias = new Date();
        en30Dias.setDate(en30Dias.getDate() + 30);
        return vencimiento > hoy && vencimiento <= en30Dias;
    };

    return (
        <DieselLayout>
            <div className="space-y-6 animate-fade-in">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl md:text-4xl font-black uppercase tracking-tight">
                            Conductores
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            {filteredConductores.length} conductor{filteredConductores.length !== 1 ? 'es' : ''}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        {hasPermission('crear_conductores') && (
                            <>
                                <ImportarExcel tipo="conductores" label="Conductores" onSuccess={loadData} />
                                <Button
                                    onClick={() => { setFormData({}); setDialogOpen(true); }}
                                    className="bg-[var(--diesel-primary)] text-[var(--diesel-secondary)]"
                                >
                                    <Plus className="h-4 w-4 mr-2" />
                                    Nuevo Conductor
                                </Button>
                            </>
                        )}
                        <Button variant="outline" onClick={loadData}>
                            <RefreshCw className="h-4 w-4" />
                        </Button>
                    </div>
                </div>

                {/* Búsqueda */}
                <Card className="border-2">
                    <CardContent className="p-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Buscar por nombre o RUT..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Tabla */}
                <Card className="border-2">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Conductor</TableHead>
                                <TableHead>RUT</TableHead>
                                <TableHead>Contacto</TableHead>
                                <TableHead>Licencia</TableHead>
                                <TableHead>Vencimiento</TableHead>
                                <TableHead>Estado</TableHead>
                                <TableHead className="text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}>
                                        <TableCell><Skeleton className="h-5 w-32" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-24" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-28" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-12" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-20" /></TableCell>
                                        <TableCell><Skeleton className="h-5 w-16" /></TableCell>
                                        <TableCell><Skeleton className="h-8 w-20 ml-auto" /></TableCell>
                                    </TableRow>
                                ))
                            ) : filteredConductores.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7}>
                                        <div className="py-12 text-center">
                                            <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                            <p className="font-medium">Sin conductores</p>
                                            <p className="text-sm text-muted-foreground">
                                                {searchTerm ? 'No se encontraron resultados' : 'Registre el primer conductor'}
                                            </p>
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                filteredConductores.map((c) => (
                                    <TableRow key={c.id}>
                                        <TableCell>
                                            <span className="font-medium">{c.nombre}</span>
                                        </TableCell>
                                        <TableCell>
                                            <span className="font-mono">{c.rut}</span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="space-y-1 text-sm">
                                                {c.telefono && (
                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                        <Phone className="h-3 w-3" />
                                                        {c.telefono}
                                                    </div>
                                                )}
                                                {c.email && (
                                                    <div className="flex items-center gap-1 text-muted-foreground">
                                                        <Mail className="h-3 w-3" />
                                                        {c.email}
                                                    </div>
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {c.licencia_clase || <span className="text-muted-foreground">-</span>}
                                        </TableCell>
                                        <TableCell>
                                            {c.vencimiento_licencia ? (
                                                <div className="flex items-center gap-1">
                                                    {isLicenciaVencida(c.vencimiento_licencia) && (
                                                        <AlertTriangle className="h-4 w-4 text-destructive" />
                                                    )}
                                                    {isLicenciaPorVencer(c.vencimiento_licencia) && (
                                                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                                                    )}
                                                    <span className={
                                                        isLicenciaVencida(c.vencimiento_licencia) 
                                                            ? 'text-destructive' 
                                                            : isLicenciaPorVencer(c.vencimiento_licencia)
                                                                ? 'text-amber-500'
                                                                : ''
                                                    }>
                                                        {new Date(c.vencimiento_licencia).toLocaleDateString()}
                                                    </span>
                                                </div>
                                            ) : (
                                                <span className="text-muted-foreground">-</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Badge variant={c.estado === 'activo' ? 'default' : 'secondary'}>
                                                {c.estado}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {hasPermission('editar_conductores') && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => { setFormData(c); setDialogOpen(true); }}
                                                    >
                                                        <Pencil className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                {hasPermission('eliminar_conductores') && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => handleDelete(c)}
                                                    >
                                                        <Trash2 className="h-4 w-4 text-destructive" />
                                                    </Button>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </Card>
            </div>

            {/* Dialog Crear/Editar */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{formData.id ? 'Editar' : 'Nuevo'} Conductor</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>RUT *</Label>
                                <Input
                                    value={formData.rut || ''}
                                    onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                                    placeholder="12.345.678-9"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Nombre *</Label>
                                <Input
                                    value={formData.nombre || ''}
                                    onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                    placeholder="Juan Pérez"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Teléfono</Label>
                                <Input
                                    value={formData.telefono || ''}
                                    onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                                    placeholder="+56 9 1234 5678"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Email</Label>
                                <Input
                                    type="email"
                                    value={formData.email || ''}
                                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                    placeholder="correo@ejemplo.cl"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Clase de Licencia</Label>
                                <select
                                    className="w-full p-2 border rounded-md bg-background"
                                    value={formData.licencia_clase || ''}
                                    onChange={(e) => setFormData({ ...formData, licencia_clase: e.target.value })}
                                    data-testid="select-licencia-clase"
                                >
                                    <option value="">Seleccionar</option>
                                    {tiposLicencia.length > 0 ? (
                                        tiposLicencia.map(tl => (
                                            <option key={tl.id} value={tl.codigo}>{tl.codigo} - {tl.nombre}</option>
                                        ))
                                    ) : (
                                        <>
                                            <option value="A1">A1 - Profesional (Taxis)</option>
                                            <option value="A2">A2 - Profesional (Transporte 10-17 pasajeros)</option>
                                            <option value="A3">A3 - Profesional (Transporte sin limite)</option>
                                            <option value="A4">A4 - Profesional (Carga +3.500 kg)</option>
                                            <option value="A5">A5 - Profesional (Carga articulada +3.500 kg)</option>
                                            <option value="B">B - No Profesional (Automoviles, camionetas)</option>
                                            <option value="C">C - No Profesional (Motocicletas)</option>
                                            <option value="D">D - Especial (Maquinaria)</option>
                                            <option value="E">E - Especial (Traccion animal)</option>
                                            <option value="F">F - Especial (Policiales, Bomberos, FF.AA.)</option>
                                        </>
                                    )}
                                </select>
                            </div>
                            <div className="space-y-2">
                                <Label>Vencimiento Licencia</Label>
                                <Input
                                    type="date"
                                    value={formData.vencimiento_licencia || ''}
                                    onChange={(e) => setFormData({ ...formData, vencimiento_licencia: e.target.value })}
                                />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Guardando...' : 'Guardar'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DieselLayout>
    );
}
